import { useMemo } from "react";
import { ClipboardCheck, Download, Loader2, Plus, Search, X } from "lucide-react";

import {
  PERMIT_APPLICATION_STATUS_LABELS,
  type PermitApplicationStatus,
} from "@/api/permit_applications";
import { PermitApplicationDetail } from "@/components/permit_suite/PermitApplicationDetail";
import { PermitTrackingCreateDialog } from "@/components/permit_suite/PermitTrackingCreateDialog";
import { SuiteTrackingLayout } from "@/components/workflow_tracking/SuiteTrackingLayout";
import {
  formatPermitDeadline,
  isPermitOverdue,
  permitApplicationListMeta,
  permitApplicationStatusLabel,
  permitApplicationStatusPillClass,
  pipelineSummaryStats,
} from "@/components/permit_suite/permitSuiteDisplay";
import type { PermitApplicationFocusRequest } from "@/components/permit_suite/permitSuiteNavigation";
import { formatRelativeTime } from "@/components/module_summary/formatRelativeTime";
import { ModuleSummaryStrip } from "@/components/module_summary/ModuleSummaryStrip";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useMunicipality } from "@/context/MunicipalityContext";
import { usePermitTrack, type PermitTrackStatusFilter } from "@/hooks/usePermitTrack";
import { cn } from "@/lib/utils";

type PermitTrackingPanelProps = {
  focusRequest?: PermitApplicationFocusRequest | null;
  onFocusRequestHandled?: () => void;
};

export function PermitTrackingPanel({
  focusRequest = null,
  onFocusRequestHandled,
}: PermitTrackingPanelProps) {
  const { municipality, municipalities, isLoading: municipalitiesLoading } = useMunicipality();
  const municipalityId = municipality?.id;
  const fileViewerEnabled = municipality?.file_viewer_access === true;

  const track = usePermitTrack({
    municipalityId,
    focusRequest,
    onFocusRequestHandled,
  });

  const summaryStats = useMemo(
    () => pipelineSummaryStats(track.applications),
    [track.applications],
  );

  if (municipalitiesLoading || municipalities.length === 0 || !municipalityId) {
    return (
      <SuiteTrackingLayout
        municipalitiesLoading={municipalitiesLoading}
        municipalitiesCount={municipalities.length}
        municipalitySelected={Boolean(municipalityId)}
        emptyIcon={ClipboardCheck}
        selectMunicipalityMessage="Select a municipality above to view permit applications."
        summaryStrip={null}
        sidebar={null}
        detail={null}
      />
    );
  }

  return (
    <SuiteTrackingLayout
      testId="permit-tracking-panel"
      municipalitiesLoading={false}
      municipalitiesCount={municipalities.length}
      municipalitySelected
      emptyIcon={ClipboardCheck}
      selectMunicipalityMessage="Select a municipality above to view permit applications."
      summaryStrip={
        <ModuleSummaryStrip
          className="border-b"
          stats={[
            { label: "Total", value: summaryStats.total },
            { label: "Active", value: summaryStats.active },
            { label: "Under review", value: summaryStats.underReview },
          ]}
          maxStats={3}
          loading={track.isLoading}
          action={{ label: "Add permit", onClick: () => track.setCreateOpen(true) }}
          trailing={
            <Button
              variant="ghost"
              size="sm"
              className="h-8 text-xs"
              disabled={track.exportingCsv}
              onClick={() => void track.handleExportCsv()}
            >
              {track.exportingCsv ? (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              ) : (
                <Download className="mr-2 h-3.5 w-3.5" />
              )}
              {track.exportingCsv ? "Exporting…" : "Export CSV"}
            </Button>
          }
        />
      }
      sidebar={
        <>
          <div className="space-y-3 border-b p-4">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Search title, applicant, or notes…"
                value={track.search}
                onChange={(event) => track.setSearch(event.target.value)}
                aria-label="Search permit applications"
              />
            </div>

            <Select
              value={track.statusFilter}
              onValueChange={(value) => track.setStatusFilter(value as PermitTrackStatusFilter)}
            >
              <SelectTrigger aria-label="Filter by status">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {track.statusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {PERMIT_APPLICATION_STATUS_LABELS[status]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {track.filtered.length > 0 ? (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Checkbox
                  id="select-all-permits"
                  checked={
                    track.selectedIds.length > 0 &&
                    track.selectedIds.length === track.filtered.length
                  }
                  onCheckedChange={(checked) => track.toggleSelectAll(checked === true)}
                />
                <Label htmlFor="select-all-permits" className="text-xs font-normal">
                  Select all
                </Label>
              </div>
            ) : null}

            {track.selectedIds.length > 0 ? (
              <div className="space-y-3 rounded-lg border bg-background p-3" data-testid="permit-bulk-actions">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium">{track.selectedIds.length} selected</p>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Clear selection"
                    onClick={() => track.setSelectedIds([])}
                  >
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Select
                    value={track.bulkStatus}
                    onValueChange={(value) => track.setBulkStatus(value as PermitApplicationStatus)}
                  >
                    <SelectTrigger className="h-8 w-[9.5rem]" aria-label="Bulk status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {track.statusOptions.map((status) => (
                        <SelectItem key={status} value={status}>
                          {PERMIT_APPLICATION_STATUS_LABELS[status]}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={track.bulkUpdateMutation.isPending}
                    onClick={() => track.bulkUpdateMutation.mutate({ status: track.bulkStatus })}
                  >
                    Set status
                  </Button>
                </div>
              </div>
            ) : null}
          </div>

          <div className="relative min-h-0 flex-1">
            <ScrollArea className="h-full min-h-0">
              <div className="space-y-2 p-3">
                {track.isLoading && (
                  <div className="flex items-center gap-2 px-2 py-6 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Loading permits…
                  </div>
                )}

                {track.error && (
                  <p className="px-2 text-sm text-destructive">Could not load permit applications.</p>
                )}

                {!track.isLoading &&
                  track.filtered.map((application) => {
                    const isSelected = track.selectedIds.includes(application.id);
                    return (
                      <div
                        key={application.id}
                        className={cn(
                          "flex gap-2 rounded-lg border bg-background p-3 transition-colors hover:bg-muted/40",
                          track.selected?.id === application.id &&
                            "border-primary/60 bg-primary/5 ring-1 ring-primary/20",
                        )}
                      >
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            track.toggleSelected(application.id, checked === true)
                          }
                          aria-label={`Select ${application.title}`}
                          className="mt-0.5"
                        />
                        <button
                          type="button"
                          className="min-w-0 flex-1 text-left"
                          onClick={() => track.setSelectedId(application.id)}
                        >
                          <span className="line-clamp-2 text-sm font-medium leading-snug">
                            {application.title}
                          </span>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span
                              className={cn(
                                "inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium",
                                permitApplicationStatusPillClass(application.status),
                              )}
                            >
                              {permitApplicationStatusLabel(application.status)}
                            </span>
                            <span className="text-[11px] text-muted-foreground">
                              {formatPermitDeadline(application.deadline_at)}
                            </span>
                            {isPermitOverdue(application.deadline_at) ? (
                              <Badge variant="destructive" className="h-5 px-1.5 text-[10px] font-medium">
                                Overdue
                              </Badge>
                            ) : null}
                          </div>
                          {permitApplicationListMeta(application) ? (
                            <p className="mt-1 truncate text-[11px] text-muted-foreground">
                              {permitApplicationListMeta(application)}
                            </p>
                          ) : null}
                          <p className="mt-1 text-[11px] text-muted-foreground">
                            Updated {formatRelativeTime(application.updated_at)}
                          </p>
                        </button>
                      </div>
                    );
                  })}

                {!track.isLoading && track.filtered.length === 0 && (
                  <div className="rounded-lg border border-dashed bg-background px-4 py-8 text-center">
                    <ClipboardCheck className="mx-auto mb-3 h-8 w-8 text-muted-foreground/50" />
                    <p className="text-sm font-medium">No permits yet</p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {track.search.trim() || track.statusFilter !== "all"
                        ? "Nothing matches your filters. Try clearing search or status."
                        : "Add your first permit application to start tracking review progress."}
                    </p>
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-4"
                      onClick={() => track.setCreateOpen(true)}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add permit
                    </Button>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </>
      }
      detail={
        track.selected ? (
          <PermitApplicationDetail
            municipalityId={municipalityId}
            fileViewerEnabled={fileViewerEnabled}
            application={track.selected}
            updating={track.updateMutation.isPending}
            onUpdate={(patch) =>
              track.updateMutation.mutate({ id: track.selected!.id, patch })
            }
            onRequestDelete={() => track.setDeleteTarget(track.selected!)}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-2 bg-background p-8 text-center text-muted-foreground">
            <ClipboardCheck className="h-10 w-10 opacity-30" />
            <p className="text-sm">Select a permit to view details</p>
          </div>
        )
      }
      footer={
        <>
          <PermitTrackingCreateDialog
        open={track.createOpen}
        onOpenChange={track.setCreateOpen}
        title={track.formTitle}
        onTitleChange={track.setFormTitle}
        applicationNumber={track.formApplicationNumber}
        onApplicationNumberChange={track.setFormApplicationNumber}
        applicantName={track.formApplicantName}
        onApplicantNameChange={track.setFormApplicantName}
        projectAddress={track.formProjectAddress}
        onProjectAddressChange={track.setFormProjectAddress}
        notes={track.formNotes}
        onNotesChange={track.setFormNotes}
        deadline={track.formDeadline}
        onDeadlineChange={track.setFormDeadline}
        saving={track.createMutation.isPending}
        onSubmit={track.handleCreate}
      />

      <AlertDialog
        open={Boolean(track.deleteTarget)}
        onOpenChange={(open) => !open && track.setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete permit application?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes {track.deleteTarget?.title} from your pipeline. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (track.deleteTarget) track.deleteMutation.mutate(track.deleteTarget.id);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        </>
      }
    />
  );
}
